<template>
  <transition name="fadeInUp">
    <v-dialog
      width="fit-content"
      max-width="900"
      elevation="0"
      v-model="modalIsOpen"
      @click:outside="toggleModal"
    >
      <div class="user_profile_modal">
        <v-card class="d-flex justify-center" color="transparent" elevation="0">
          <h4 class="modal_title">{{ $t("TITLES.editProfile") }}</h4>

          <form @submit.prevent="validateFormInputs">
            <div class="row">
              <!-- Start:: Profile Image Upload Input -->
              <base-image-upload-input
                col="12"
                identifier="admin_image"
                :preSelectedImage="data?.image?.path"
                :placeholder="$t('PLACEHOLDERS.personalImage')"
                @selectImage="selectImage"
              />
              <!-- End:: Profile Image Upload Input -->

              <!-- Start:: Name Input -->
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.name')"
                v-model.trim="data.name"
                :maxlength="100"
                required
              />
              <!-- End:: Name Input -->

              <!-- Start:: Mobile Input -->
              <base-input
                col="6"
                type="tel"
                :placeholder="$t('PLACEHOLDERS.phone')"
                v-model.trim="data.phone"
                :maxlength="10"
                required
              />
              <!-- End:: Mobile Input -->

              <!-- Start:: Email Input -->
              <base-input
                col="12"
                type="text"
                :placeholder="$t('PLACEHOLDERS.email')"
                v-model.trim="data.email"
                required
              />
              <!-- End:: Email Input -->

              <!-- Start:: Activity Type Select -->
              <base-select-input
                col="6"
                :optionsList="activityTypes"
                :placeholder="$t('PLACEHOLDERS.activityType')"
                :value="data.activityType"
                @input="data.activityType = $event"
                label="name"
                trackBy="value"
                required
              />
              <!-- End:: Activity Type Select -->

              <!-- Start:: Warehouse Address Input -->
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.warehouseAddress')"
                v-model.trim="data.warehouseAddress"
              />
              <!-- End:: Warehouse Address Input -->

              <!-- Start:: Commercial Registration Image -->
              <base-image-upload-input
                col="6"
                identifier="commercial_registration"
                :preSelectedImage="data.commercialRegistration?.path"
                :placeholder="$t('PLACEHOLDERS.commercialRegistration')"
                @selectImage="selectCommercialRegistration"
                required
              />
              <!-- End:: Commercial Registration Image -->

              <!-- Start:: Tax Registration Image -->
              <base-image-upload-input
                col="6"
                identifier="tax_registration"
                :preSelectedImage="data.taxRegistration?.path"
                :placeholder="$t('PLACEHOLDERS.taxRegistration')"
                @selectImage="selectTaxRegistration"
                required
              />
              <!-- End:: Tax Registration Image -->

              <!-- Start:: Map Location -->
              <div class="col-12 my-3 map_location_section">
                <label class="form-label map_location_label">
                  {{ $t('PLACEHOLDERS.mapLocation') }}
                </label>
                <changable-map-component
                  :lat="data.lat"
                  :lng="data.lng"
                  @update:lat="data.lat = $event"
                  @update:lng="data.lng = $event"
                  @update:placeName="data.address = $event"
                />
              </div>
              <!-- End:: Map Location -->

              <!-- Start:: Address Input (from map, read-only) -->
              <div class="map_address_field">
                <base-input
                  col="12"
                  type="text"
                  :placeholder="$t('PLACEHOLDERS.address')"
                  v-model.trim="data.address"
                  disabled
                  required
                />
              </div>
              <!-- End:: Address Input -->

              <!-- Start:: Activate Edit Password Switch Input -->
              <div class="input_wrapper switch_wrapper my-5">
                <v-switch
                  color="green"
                  :label="$t('PLACEHOLDERS.editPassword')"
                  v-model="data.enableEditPassword"
                  hide-details
                ></v-switch>
              </div>
              <!-- End:: Activate Edit Password Switch Input -->

              <Transition name="fadeInUp" mode="out-in">
                <div class="col-12" v-if="data.enableEditPassword">
                  <div class="row">
                    <!-- Start:: Password Input -->
                    <base-input
                      col="6"
                      type="password"
                      :placeholder="$t('PLACEHOLDERS.password')"
                      v-model.trim="data.password"
                      required
                    />
                    <!-- End:: Password Input -->

                    <!-- Start:: Confirm Password Input -->
                    <base-input
                      col="6"
                      type="password"
                      :placeholder="$t('PLACEHOLDERS.confirmPassword')"
                      v-model.trim="data.passwordConfirmation"
                      required
                    />
                    <!-- End:: Confirm Password Input -->
                  </div>
                </div>
              </Transition>

              <!-- Start:: Submit Button Wrapper -->
              <div class="btn_wrapper d-flex justify-content-center gap-2">
                <base-button
                  class="mt-2"
                  styleType="primary_btn"
                  :btnText="$t('BUTTONS.save')"
                  :isLoading="isWaitingRequest"
                  :disabled="isWaitingRequest"
                />

                <base-button
                  class="mt-2"
                  type="button"
                  styleType="cancel_btn"
                  :btnText="$t('BUTTONS.cancel')"
                  @fireClick="toggleModal"
                />
              </div>
              <!-- End:: Submit Button Wrapper -->
            </div>
          </form>
        </v-card>
      </div>
    </v-dialog>
  </transition>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ChangableMapComponent from "@/components/general/ChangableMapComponent.vue";

export default {
  name: "UserDataModal",

  components: {
    ChangableMapComponent,
  },

  emits: ["toggleModal"],

  props: {
    modalIsOpen: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data
      
      // Start:: Activity Types List
      activityTypes: [],
      // End:: Activity Types List

      // Start:: Data Collection To Send
      logoutConfirmationModalIsOpen: false,
      data: {
        image: {
          path: null,
          file: null,
          storagePath: null,
          url: null,
        },
        name: null,
        email: null,
        phone: null,
        activityType: null,
        warehouseAddress: null,
        address: null,
        commercialRegistration: {
          file: null,
          path: null,
          storagePath: null,
        },
        taxRegistration: {
          file: null,
          path: null,
          storagePath: null,
        },
        lat: 24.7136,
        lng: 46.6753,
        enableEditPassword: false,
        password: null,
        passwordConfirmation: null,
      },
      // End:: Data Collection To Send
    };
  },

  watch: {
    modalIsOpen(newVal) {
      if (newVal) {
        this.getDataToEdit();
      }
    },
  },

  computed: {
    // Start:: Vuex Getters
    ...mapGetters({
      authenticatedUserData: "AuthenticationModule/getAuthenticatedUserData",
    }),
    // End:: Vuex Getters
  },

  methods: {
    // Start:: Control Modal Apperance
    toggleModal() {
      this.$emit("toggleModal");
      if (!this.modalIsOpen) {
        this.data.image = { path: null, file: null };
      } else {
        this.getDataToEdit();
      }
      this.data.password = null;
      this.data.passwordConfirmation = null;
      this.data.enableEditPassword = false;
    },
    // End:: Control Modal Apperance

    // Start:: Vuex Actions
    ...mapActions({
      setAuthenticatedUserData: "AuthenticationModule/setAuthenticatedUserData",
      logout: "AuthenticationModule/logout",
    }),
    // End:: Vuex Actions

    // Start:: Select Upload Image
    selectImage(selectedImage) {
      this.data.image = selectedImage;
    },
    // End:: Select Upload Image

    // Start:: Select Commercial Registration
    selectCommercialRegistration(selectedImage) {
      this.data.commercialRegistration = selectedImage;
    },
    // End:: Select Commercial Registration

    // Start:: Select Tax Registration
    selectTaxRegistration(selectedImage) {
      this.data.taxRegistration = selectedImage;
    },
    // End:: Select Tax Registration

    // Start:: Get Data To Edit
    async getDataToEdit() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `profile`,
        });

        // Start:: Set Data
        this.data.image.path = res.data.user.image?.url || null;
        this.data.name = res.data.user.name;
        this.data.email = res.data.user.email;
        this.data.phone = res.data.user.mobile;
        
        // Update store with latest profile data
        this.setAuthenticatedUserData({
          name: res.data.user.name,
          email: res.data.user.email,
          image: res.data.user.image?.url || null,
        });
        // Set provider info
        if (res.data.user.provider_info) {
          const providerInfo = res.data.user.provider_info;
          
          // Set activity type
          const activityTypeValue = providerInfo.type;
          this.data.activityType = this.activityTypes.find(
            type => type.value === activityTypeValue
          );
          
          this.data.warehouseAddress = providerInfo.warehouse_address;
          this.data.address = providerInfo.address || "";
          
          // Set commercial registration
          if (providerInfo.commercial_registration_image?.url) {
            this.data.commercialRegistration.path = providerInfo.commercial_registration_image.url;
          }
          
          // Set tax registration
          if (providerInfo.tax_registration_image?.url) {
            this.data.taxRegistration.path = providerInfo.tax_registration_image.url;
          }
          
          // Set map coordinates
          if (providerInfo.lat && providerInfo.lng) {
            this.data.lat = parseFloat(providerInfo.lat);
            this.data.lng = parseFloat(providerInfo.lng);
          }
        }
        // End:: Set Data
      } catch (error) {
        console.log(error.response.data.message);
      }
    },
    // End:: Get Data To Edit

    // Start:: validate Form Inputs
    validateFormInputs() {
      this.isWaitingRequest = true;
      
      // Name validation
      if (!this.data.name) {
        this.isWaitingRequest = false;
        this.$message.error("يجب ادخال الاسم");
        return;
      }
      if (this.data.name.length < 2) {
        this.isWaitingRequest = false;
        this.$message.error("الحد الأدنى حرفين");
        return;
      }
      // Validate name accepts only letters and numbers
      const nameRegex = /^[a-zA-Z0-9\u0600-\u06FF\s]+$/;
      if (!nameRegex.test(this.data.name)) {
        this.isWaitingRequest = false;
        this.$message.error("يقبل فقط حروف وأرقام");
        return;
      }

      // Mobile validation
      if (!this.data.phone) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.mobile"));
        return;
      }
      // Validate Saudi mobile format (starts with 05 and exactly 10 digits)
      const mobileRegex = /^05\d{8}$/;
      if (!mobileRegex.test(this.data.phone)) {
        this.isWaitingRequest = false;
        this.$message.error("رقم الجوال يجب أن يبدأ بـ 05 ويحتوي على 10 أرقام");
        return;
      }

      // Email validation
      if (!this.data.email) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.email"));
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.data.email)) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.email_domain"));
        return;
      }

      // Activity type validation
      if (!this.data.activityType) {
        this.isWaitingRequest = false;
        this.$message.error("يجب اختيار نوع النشاط");
        return;
      }

      // Address validation (must be set from map)
      if (!this.data.address) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.addressFromMap"));
        return;
      }

      // Commercial registration validation (required except for individual)
      if (this.data.activityType.value !== 'individual' && !this.data.commercialRegistration.storagePath && !this.data.commercialRegistration.path) {
        this.isWaitingRequest = false;
        this.$message.error("يجب رفع صورة السجل التجاري");
        return;
      }

      // Tax registration validation (required except for individual)
      if (this.data.activityType.value !== 'individual' && !this.data.taxRegistration.storagePath && !this.data.taxRegistration.path) {
        this.isWaitingRequest = false;
        this.$message.error("يجب رفع صورة السجل الضريبي");
        return;
      }

      // Password validation if editing password
      if (this.data.enableEditPassword) {
        if (!this.data.password) {
          this.isWaitingRequest = false;
          this.$message.error(this.$t("VALIDATION.password"));
          return;
        }
        if (this.data.password.length < 6) {
          this.isWaitingRequest = false;
          this.$message.error("كلمة المرور يجب ألا تقل عن 6 خانات");
          return;
        }
        if (!this.data.passwordConfirmation) {
          this.isWaitingRequest = false;
          this.$message.error(this.$t("VALIDATION.password"));
          return;
        }
        if (this.data.passwordConfirmation.length < 6) {
          this.isWaitingRequest = false;
          this.$message.error("تأكيد كلمة المرور يجب ألا يقل عن 6 خانات");
          return;
        }
        if (this.data.password !== this.data.passwordConfirmation) {
          this.isWaitingRequest = false;
          this.$message.error("كلمة المرور وتأكيد كلمة المرور غير متطابقين");
          return;
        }
      }

      this.submitForm();
    },
    // End:: validate Form Inputs

    // Start:: Submit Form
    async submitForm() {
      const REQUEST_DATA = new FormData();
      
      // Start:: Append Request Data
      if (this.data.image.storagePath) {
        REQUEST_DATA.append("avatar", this.data.image.storagePath);
      }
      REQUEST_DATA.append("name", this.data.name);
      REQUEST_DATA.append("mobile", this.data.phone);
      REQUEST_DATA.append("email", this.data.email);
      
      // Append activity type
      if (this.data.activityType) {
        REQUEST_DATA.append("provider_info[type]", this.data.activityType.value);
      }
      
      // Append warehouse address
      if (this.data.warehouseAddress) {
        REQUEST_DATA.append("provider_info[warehouse_address]", this.data.warehouseAddress);
      }

      // Append address from map
      if (this.data.address) {
        REQUEST_DATA.append("provider_info[address]", this.data.address);
      }
      
      // Append commercial registration
      if (this.data.commercialRegistration.storagePath) {
        REQUEST_DATA.append("provider_info[commercial_registration_image]", this.data.commercialRegistration.storagePath);
      }
      
      // Append tax registration
      if (this.data.taxRegistration.storagePath) {
        REQUEST_DATA.append("provider_info[tax_registration_image]", this.data.taxRegistration.storagePath);
      }
      
      // Append map coordinates
      REQUEST_DATA.append("provider_info[lat]", this.data.lat);
      REQUEST_DATA.append("provider_info[lng]", this.data.lng);
      
      // Append password if editing
      if (this.data.enableEditPassword) {
        REQUEST_DATA.append("password", this.data.password);
        REQUEST_DATA.append("password_confirmation", this.data.passwordConfirmation);
      }
      // End:: Append Request Data

      try {
        let res = await this.$axios({
          method: "POST",
          url: "profile",
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.editedSuccessfully"));
        this.setAuthenticatedUserData({
          name: res.data.data.user.name,
          email: res.data.data.user.email,
          image: res.data.data.user.image?.url,
        });
        this.toggleModal();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(error.response?.data?.message || "حدث خطأ أثناء التحديث");
      }
    },
    // End:: Submit Form
  },

  created() {
    // Initialize activity types with translations
    this.activityTypes = [
      { 
        name: this.$t('PLACEHOLDERS.logistics_company'), 
        value: "logistics_company" 
      },
      { 
        name: this.$t('PLACEHOLDERS.ecommerce_store'), 
        value: "ecommerce_store" 
      },
      { 
        name: this.$t('PLACEHOLDERS.traditional_store'), 
        value: "traditional_store" 
      },
      { 
        name: this.$t('PLACEHOLDERS.individual'), 
        value: "individual" 
      },
    ];
  },
};
</script>

<style lang="scss">
.user_profile_modal {
  padding-block: 15px;

  .modal_title {
    margin-bottom: 20px;
    text-align: center;
    color: var(--main_theme_clr);
  }

  .map_location_label {
    display: inline-block;
    margin-bottom: 8px;
    font-size: 16px;
    font-weight: 600;
    color: var(--main_theme_clr);
  }

  .map_address_field {
    .input_wrapper.top_label {
      position: relative;
      border-color: rgba(239, 52, 5, 0.28);
      background: linear-gradient(
        135deg,
        rgba(239, 52, 5, 0.05) 0%,
        var(--theme_second_bg_clr, #fff) 100%
      );

      &::after {
        content: "\f3c5";
        font-family: "Font Awesome 5 Pro";
        font-weight: 300;
        position: absolute;
        top: 50%;
        inset-inline-start: 14px;
        transform: translateY(-50%);
        color: var(--main_theme_clr);
        opacity: 0.6;
        font-size: 15px;
        pointer-events: none;
      }

      .form-label {
        color: var(--main_theme_clr);
        font-weight: 600;
      }

      input:disabled {
        padding-inline-start: 36px;
        color: var(--theme_text_clr);
        background-color: transparent !important;
        opacity: 1;
        cursor: default;
        -webkit-text-fill-color: var(--theme_text_clr);
      }
    }
  }
}

.dark_theme .user_profile_modal {
  .map_location_label {
    color: #ff6b42;
  }

  .map_address_field {
    .input_wrapper.top_label {
      border-color: rgba(239, 52, 5, 0.55);
      background: linear-gradient(
        135deg,
        rgba(239, 52, 5, 0.14) 0%,
        #1e1e1e 50%,
        #262626 100%
      );
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);

      &::after {
        color: #ff6b42;
        opacity: 0.9;
      }

      .form-label {
        background-color: #1a1a1a;
        color: #ff6b42;
      }

      input:disabled {
        color: #f2f2f2;
        -webkit-text-fill-color: #f2f2f2;
      }

      input:disabled::placeholder {
        color: rgba(255, 255, 255, 0.42);
        -webkit-text-fill-color: rgba(255, 255, 255, 0.42);
      }
    }
  }
}
</style>
